import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Shield, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { SECRET_SLOTS_FOR_AUTH, SITECENTRIC_AUTH } from "./_shared";
import SiteCentricAuthPanel from "./SiteCentricAuthPanel";

/**
 * Wizard Step 2 — Auth & Secrets.
 *
 * Renders the secret-slot table for the connection's current auth_type
 * (api_key / bearer / basic / oauth / custom_header). Plaintext is
 * never returned after save — rotate replaces, retire deactivates.
 *
 * Secrets are AES-256-GCM encrypted at rest with the per-tenant DEK
 * (`connection_secret_store.add_secret`). Both the slot table and the
 * historical retired-secret list are read-only when ``immutable``.
 */
export default function AuthSecretsStep({
    conn, secrets, immutable, canEdit, canManageSecret, canTest, onSaved,
}) {
    const slots = SECRET_SLOTS_FOR_AUTH[conn.auth_type] || [];
    const [adding, setAdding] = useState(null); // slot name
    const [value, setValue] = useState("");
    const [saving, setSaving] = useState(false);

    if (conn.auth_type === SITECENTRIC_AUTH) {
        return <SiteCentricAuthPanel conn={conn} immutable={immutable} canEdit={canEdit} canTest={canTest} onSaved={onSaved} />;
    }

    const submit = async () => {
        if (!value) return;
        setSaving(true);
        try {
            await api.post(`/admin/connections/${conn.id}/secrets`, {
                secret_type: adding, value,
            });
            toast.success(`${adding} stored — value never returned`);
            setAdding(null); setValue("");
            onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const retire = async (secret) => {
        if (!window.confirm(`Retire this ${secret.secret_type}? Outbound calls will fall back to the next-active secret of the same type.`)) return;
        try {
            await api.delete(`/admin/connections/${conn.id}/secrets/${secret.id}`);
            toast.success("Secret retired");
            onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-3" data-testid="wizard-auth-step">
            <div className="px-3 py-2 rounded-md border border-violet-300/20 bg-violet-300/[0.04] text-[11px] text-violet-200/90 inline-flex items-center gap-2 w-full">
                <Shield className="w-3 h-3" />
                Secrets are AES-256-GCM encrypted at rest with the per-tenant DEK. Plaintext is never returned after save — rotate to replace, retire to disable.
            </div>

            <div className="border border-white/10 rounded-md overflow-hidden">
                <div className="px-3 py-2 bg-white/[0.04] text-[10px] uppercase tracking-wider text-white/50 flex items-center justify-between">
                    <span>Slots required for <span className="font-mono">{conn.auth_type}</span></span>
                    <span className="text-white/35 normal-case tracking-normal">{secrets.length} stored</span>
                </div>
                {slots.length === 0 ? (
                    <div className="px-3 py-6 text-center text-xs text-white/40" data-testid="wizard-auth-no-slots">
                        Auth type <span className="font-mono">none</span> requires no secrets.
                    </div>
                ) : (
                    <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-wider text-white/50">
                            <tr>
                                <th className="text-left px-3 py-1.5">Slot</th>
                                <th className="text-left px-3 py-1.5">Active value</th>
                                <th className="text-left px-3 py-1.5">Status</th>
                                <th className="text-left px-3 py-1.5">Stored</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {slots.map((slot) => {
                                const active = secrets.find((s) => s.secret_type === slot && s.active);
                                return (
                                    <tr key={slot} className="border-t border-white/5"
                                        data-testid={`secret-slot-${slot}`}>
                                        <td className="px-3 py-1.5 font-mono">{slot}</td>
                                        <td className="px-3 py-1.5 font-mono text-white/70">
                                            {active ? active.masked_display_value : <span className="text-white/30">— none —</span>}
                                        </td>
                                        <td className="px-3 py-1.5">
                                            {active ? (
                                                <span className="text-emerald-200 inline-flex items-center gap-1">
                                                    <CheckCircle2 className="w-3 h-3" /> active
                                                </span>
                                            ) : (
                                                <span className="text-rose-200 inline-flex items-center gap-1">
                                                    <AlertTriangle className="w-3 h-3" /> missing
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-1.5 text-white/55 font-mono text-[10px]">
                                            {active ? (active.created_at || "").slice(0, 16).replace("T", " ") : "—"}
                                        </td>
                                        <td className="px-3 py-1.5 text-right">
                                            {!immutable && canManageSecret && (
                                                <div className="inline-flex gap-1">
                                                    <button onClick={() => { setAdding(slot); setValue(""); }}
                                                        data-testid={`secret-add-${slot}`}
                                                        className="px-2 py-0.5 text-[10px] border border-white/15 rounded hover:bg-white/5">
                                                        {active ? "Rotate" : "Set"}
                                                    </button>
                                                    {active && (
                                                        <button onClick={() => retire(active)}
                                                            data-testid={`secret-retire-${slot}`}
                                                            className="px-2 py-0.5 text-[10px] border border-rose-300/20 text-rose-200 rounded hover:bg-rose-300/10">
                                                            Retire
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Historical secrets — non-active rows */}
            {secrets.filter((s) => !s.active).length > 0 && (
                <details className="text-xs">
                    <summary className="cursor-pointer text-white/50 hover:text-white">
                        Show {secrets.filter((s) => !s.active).length} retired secret(s)
                    </summary>
                    <table className="w-full text-xs mt-2">
                        <thead className="text-[10px] uppercase tracking-wider text-white/50">
                            <tr>
                                <th className="text-left px-3 py-1.5">Type</th>
                                <th className="text-left px-3 py-1.5">Masked</th>
                                <th className="text-left px-3 py-1.5">Retired</th>
                            </tr>
                        </thead>
                        <tbody>
                            {secrets.filter((s) => !s.active).map((s) => (
                                <tr key={s.id} className="border-t border-white/5">
                                    <td className="px-3 py-1.5 font-mono">{s.secret_type}</td>
                                    <td className="px-3 py-1.5 font-mono text-white/55">{s.masked_display_value}</td>
                                    <td className="px-3 py-1.5 text-white/45 font-mono text-[10px]">
                                        {(s.retired_at || "").slice(0, 16).replace("T", " ") || "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </details>
            )}

            {adding && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    data-testid="secret-add-modal">
                    <div className="w-full max-w-md bg-[#0c0c0c] border border-white/10 rounded-xl p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-mono">Set <span className="text-violet-300">{adding}</span></h3>
                            <button onClick={() => setAdding(null)} className="opacity-60 hover:opacity-100">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="text-[11px] text-white/55 space-y-1">
                            <div>Plaintext is encrypted before storage and never returned again. Rotation deactivates the previous active secret of this type.</div>
                        </div>
                        <input type="password" autoFocus value={value} onChange={(e) => setValue(e.target.value)}
                            placeholder="Paste the secret value"
                            data-testid="secret-add-value"
                            className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
                        <div className="flex justify-end gap-2 pt-1">
                            <button onClick={() => setAdding(null)}
                                className="px-3 py-1.5 text-xs border border-white/15 rounded-md hover:bg-white/5">
                                Cancel
                            </button>
                            <button onClick={submit} disabled={!value || saving}
                                data-testid="secret-add-save"
                                className="px-3 py-1.5 text-xs bg-white text-black rounded-md disabled:opacity-40">
                                {saving ? "Saving…" : "Save secret"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
